import { calendarMonthBounds } from "../domain/reconciliation";
import type { ProfitMode } from "../shared/profit-types";

export function profitQueryString(input: {
  storeId: string;
  platform?: "" | "amazon" | "tiktok";
  site?: string;
  month: string;
  reportingCurrency: "CNY" | "USD";
  profitMode: ProfitMode;
  granularity?: "day" | "week" | "month" | "quarter" | "year";
  periodAnchor?: string;
  extra?: Record<string, string | undefined>;
}) {
  const bounds = profitDateBounds(input.granularity ?? "month", input.periodAnchor ?? `${input.month}-01`, input.month);
  const values: Record<string, string> = {
    dateFrom: bounds.start,
    dateTo: bounds.end,
    reportingCurrency: input.reportingCurrency,
    mode: input.profitMode
  };
  if (input.storeId) values.storeId = input.storeId;
  if (input.platform) values.platform = input.platform;
  if (input.site) values.site = input.site;
  for (const [key, value] of Object.entries(input.extra ?? {})) if (value) values[key] = value;
  return new URLSearchParams(values).toString();
}

export function profitDateBounds(granularity: "day" | "week" | "month" | "quarter" | "year", anchor: string, month: string) {
  if (granularity === "month") return calendarMonthBounds(month);
  const source = granularity === "quarter" || granularity === "year" ? `${month}-01` : anchor;
  const parsed = new Date(`${source}T00:00:00Z`);
  if (Number.isNaN(parsed.getTime())) return calendarMonthBounds(month);
  if (granularity === "day") return { start: source, end: source };
  if (granularity === "week") {
    const day = parsed.getUTCDay() || 7;
    const start = new Date(parsed);
    start.setUTCDate(start.getUTCDate() - day + 1);
    const end = new Date(start);
    end.setUTCDate(end.getUTCDate() + 6);
    return { start: isoDate(start), end: isoDate(end) };
  }
  if (granularity === "quarter") {
    const startMonth = Math.floor(parsed.getUTCMonth() / 3) * 3;
    const start = new Date(Date.UTC(parsed.getUTCFullYear(), startMonth, 1));
    const end = new Date(Date.UTC(parsed.getUTCFullYear(), startMonth + 3, 0));
    return { start: isoDate(start), end: isoDate(end) };
  }
  return { start: `${parsed.getUTCFullYear()}-01-01`, end: `${parsed.getUTCFullYear()}-12-31` };
}

export function profitPeriodLabel(granularity: "day" | "week" | "month" | "quarter" | "year", anchor: string, month: string) {
  const bounds = profitDateBounds(granularity, anchor, month);
  if (granularity === "day") return bounds.start;
  if (granularity === "week") return `${bounds.start} 至 ${bounds.end}`;
  if (granularity === "month") return month;
  if (granularity === "quarter") return `${bounds.start.slice(0, 4)} Q${Math.floor((Number(bounds.start.slice(5, 7)) - 1) / 3) + 1}`;
  return bounds.start.slice(0, 4);
}

function isoDate(value: Date) {
  return value.toISOString().slice(0, 10);
}
